import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { IPlayedLines, IRide, IRideMapNumberIcons, IRideMapNumberIconTooltips } from '../types';
import {
  activeRideIndexAtom,
  activeRideNumberAtom,
  actualPlayPositionAtom,
  selectedRidesAtom,
  showAllRidesAtom,
  stopAtom,
} from '../RidePlayerState';
import { numberMarkerIcon, numberMarkerIconDisabled } from '../RidePlayerIconService';
import { resetAllPlayedRideLines, updateTimelinePositionMarker } from '../RidePlayerService';
import {
  createRideNumberMarkers,
  createRideNumberMarkerTooltips,
} from '../RidePlayerMarkerService';

interface IUseRideNumberMarker {
  rideNumberMarkers: IRideMapNumberIcons;
  resetAllRideNumberMarkersZIndex: () => void;
  setRideNumberMarkerZIndexForRide: (rideNumber: number, zIndex: number) => void;
  setAllRideNumberMarkerDisabled: (exceptRideNumber?: number) => void;
  setAllRideNumberMarkerEnabled: (exceptRideNumber?: number) => void;
  setRideNumberMarkerEnabled: (rideNumber: number) => void;
}

const useRideNumberMarker = (
  map: google.maps.Map,
  rides: IRide[],
  setIsRideActive: React.Dispatch<React.SetStateAction<number | false>>,
  playedRideLines: IPlayedLines,
  timelinePositionMarker: google.maps.Marker | null,
  timelinePositionMarkerTooltip: google.maps.OverlayView | null
): IUseRideNumberMarker => {
  const intl = useIntl();
  const rideNumberMarkers = React.useRef<IRideMapNumberIcons>({});
  const rideNumberMarkerTooltips = React.useRef<IRideMapNumberIconTooltips>({});
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const setActiveRideIndex = useSetRecoilState<number>(activeRideIndexAtom);
  const setActualPosition = useSetRecoilState<number>(actualPlayPositionAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);
  const setStop = useSetRecoilState<boolean>(stopAtom);
  const setShowAllRides = useSetRecoilState<boolean>(showAllRidesAtom);
  const [rideNumberMarkersCreated, setRideNumberMarkersCreated] = React.useState<boolean>(false);

  /**
   * Reset all ride number markers zindexes to its original values
   * @returns {void}
   */
  const resetAllRideNumberMarkersZIndex = (): void => {
    if (rideNumberMarkers.current) {
      Object.keys(rideNumberMarkers.current).forEach((rideNumber: string) => {
        const marker: google.maps.Marker = rideNumberMarkers.current[rideNumber];
        if (marker) {
          marker.setZIndex(marker.get('originalZIndex'));
        }
      });
    }
  };

  /**
   * Set zIndex for ride number marker for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setRideNumberMarkerZIndexForRide = (rideNumber: number, zIndex: number): void => {
    if (rideNumberMarkers.current) {
      const marker = rideNumberMarkers.current[rideNumber] as google.maps.Marker;
      if (marker) {
        marker.setZIndex(zIndex);
      }
    }
  };

  /**
   * Set all ride number icon markers to disabled
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllRideNumberMarkerDisabled = (exceptRideNumber?: number): void => {
    if (rideNumberMarkers.current) {
      Object.keys(rideNumberMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const marker = rideNumberMarkers.current[rideNumber] as google.maps.Marker;
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: numberMarkerIconDisabled(Number(rideNumber)),
            anchor: new google.maps.Point(15, 15),
          };
          marker.setIcon(newIcon);
        }
      });
    }
  };

  /**
   * Set all ride number icon markers to enabled
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllRideNumberMarkerEnabled = (exceptRideNumber?: number): void => {
    if (rideNumberMarkers.current) {
      Object.keys(rideNumberMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const marker = rideNumberMarkers.current[rideNumber] as google.maps.Marker;
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: numberMarkerIcon(Number(rideNumber)),
            anchor: new google.maps.Point(15, 15),
          };
          marker.setIcon(newIcon);
        }
      });
    }
  };

  /**
   * Set ride number icon marker to enabled for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const setRideNumberMarkerEnabled = (rideNumber: number): void => {
    if (rideNumberMarkers.current) {
      const marker = rideNumberMarkers.current[rideNumber] as google.maps.Marker;
      if (marker) {
        const newIcon: google.maps.Icon = {
          url: numberMarkerIcon(Number(rideNumber)),
          anchor: new google.maps.Point(15, 15),
        };
        marker.setIcon(newIcon);
      }
    }
  };

  /**
   * Toggle marker tooltip handler
   * @param {number} rideNumber
   * @param {number{ positionIndex
   * @returns {void}
   */
  const handleToggleTooltip = React.useCallback(
    (rideNumber: number): void => {
      if (rideNumberMarkerTooltips.current[rideNumber]) {
        const currentMarkerTooltip = rideNumberMarkerTooltips.current[rideNumber];
        if (currentMarkerTooltip) {
          currentMarkerTooltip.setMap(currentMarkerTooltip.getMap() ? null : map);
        }
      }
    },
    [map]
  );

  /**
   * Set active ride by clicking to ride number icon
   * @param {number} rideNumber
   * @returns {void}
   */
  const handleActiveRide = React.useCallback(
    (rideNumber: number): void => {
      const rideIndex = selectedRides.indexOf(rideNumber);
      if (rideIndex !== -1) {
        setActiveRideIndex(rideIndex);
        setIsRideActive(rideNumber);
        setActualPosition(0);
        setShowAllRides(false);
        setStop(false);
        resetAllPlayedRideLines(playedRideLines);
        updateTimelinePositionMarker(
          timelinePositionMarker,
          timelinePositionMarkerTooltip,
          intl,
          0,
          rides[activeRide - 1].positions[0],
          rides[activeRide - 1].rideData
        );
      }
    },
    [
      activeRide,
      intl,
      playedRideLines,
      rides,
      selectedRides,
      setActiveRideIndex,
      setActualPosition,
      setIsRideActive,
      setShowAllRides,
      setStop,
      timelinePositionMarker,
      timelinePositionMarkerTooltip,
    ]
  );

  // Show/hide ride number icon markers when selected rides changes
  React.useEffect(() => {
    Object.keys(rideNumberMarkers.current).forEach((rideNumber: string) => {
      const marker = rideNumberMarkers.current[Number(rideNumber)];
      if (!selectedRides.includes(Number(rideNumber))) {
        (marker as google.maps.OverlayView).setMap(null);
      }
      if (selectedRides.includes(Number(rideNumber))) {
        (marker as google.maps.OverlayView).setMap(map);
      }
    });
  }, [map, selectedRides]);

  // Create ride number icon markers and tooltips
  React.useEffect(() => {
    if (rides.length > 1 && selectedRides.length > 0 && !rideNumberMarkersCreated) {
      // when position of some ride is empty
      const filterEmptyRides = rides.filter((ride) => ride.positions.length > 0);
      rideNumberMarkers.current = createRideNumberMarkers(
        map,
        filterEmptyRides,
        handleToggleTooltip,
        handleActiveRide
      );
      rideNumberMarkerTooltips.current = createRideNumberMarkerTooltips(filterEmptyRides, intl);
      setRideNumberMarkersCreated(true);
    }
    // return () => {
    //   rideNumberMarkers.current = {};
    // };
  }, [
    handleActiveRide,
    handleToggleTooltip,
    intl,
    map,
    rides,
    selectedRides,
    rideNumberMarkersCreated,
  ]);

  React.useEffect(() => {
    return () => {
      rideNumberMarkers.current = {};
    };
  }, []);

  return {
    rideNumberMarkers: rideNumberMarkers.current,
    setRideNumberMarkerZIndexForRide,
    resetAllRideNumberMarkersZIndex,
    setAllRideNumberMarkerDisabled,
    setAllRideNumberMarkerEnabled,
    setRideNumberMarkerEnabled,
  };
};

export default useRideNumberMarker;
